/**
 * TransactionDetail Component
 *
 * Modal displaying full details for a single transaction.
 * Includes HashScan link for on-chain verification,
 * network info, linked user account, and fee breakdown.
 *
 * @component
 */

import React from "react";
import { modalStyles, colors } from "./styles";
import {
    formatDate,
    formatAmount,
    getHashscanUrl,
    getCategoryBadgeStyle,
    getDirectionBadgeStyle,
    getStatusBadgeStyle,
} from "./utils";

// ============================================================================
// Helpers
// ============================================================================

/**
 * Extract fee from transaction metadata (same pattern as table)
 */
const getTransactionFee = (tx) => {
    // For settlement transactions, use the fee field directly
    if (tx.isSettlement && tx.fee) {
        return parseFloat(tx.fee) || 0;
    }
    
    const metadata = tx.metadata;
    if (!metadata) return 0;
    if (metadata.swapDetails?.fee) return parseFloat(metadata.swapDetails.fee) || 0;
    if (metadata.fee) return parseFloat(metadata.fee) || 0;
    return 0;
};

/**
 * Check if transaction is a settlement/prediction transaction
 */
const isSettlementTransaction = (tx) => {
    return tx.isSettlement === true || (tx.type === 'settlement') || (tx.transactionType === 'SETTLEMENT');
};

/**
 * Format network name for display
 */
const formatNetwork = (network) => {
    if (!network) return "Hedera";
    return `Hedera ${network.charAt(0).toUpperCase() + network.slice(1)}`;
};

// ============================================================================
// Detail Row
// ============================================================================

const DetailRow = ({ label, children }) => (
    <div style={modalStyles.detailRow}>
        <span style={modalStyles.detailLabel}>{label}</span>
        <div style={modalStyles.detailValue}>{children}</div>
    </div>
);

// ============================================================================
// Main Component
// ============================================================================

const TransactionDetail = ({ transaction, wallet, hederaNetwork, onClose, getCurrencyDisplay }) => {
    if (!transaction) return null;

    const categoryStyle = getCategoryBadgeStyle(transaction.category);
    const directionStyle = getDirectionBadgeStyle(transaction.direction);
    const statusStyle = getStatusBadgeStyle(transaction.status);
    const fee = getTransactionFee(transaction);
    const amount = parseFloat(transaction.amount) || 0;
    const total = amount + fee;
    
    // Check if this is a settlement transaction
    const isSettlement = isSettlementTransaction(transaction);
    const totalDeducted = isSettlement && transaction.totalDeducted 
        ? parseFloat(transaction.totalDeducted) 
        : amount + fee;

    return (
        <div style={modalStyles.overlay} onClick={onClose}>
            <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={modalStyles.header}>
                    <h3 style={modalStyles.headerTitle}>
                        {isSettlement ? "Settlement Transaction Details" : "Transaction Details"}
                    </h3>
                    <button
                        type="button"
                        style={modalStyles.closeButton}
                        onClick={onClose}
                    >
                        ✕ Close
                    </button>
                </div>

                {/* Body */}
                <div style={modalStyles.body}>
                    {isSettlement ? (
                        // Settlement Transaction Layout
                        <>
                            <DetailRow label="Settlement ID">
                                <code
                                    style={{
                                        background: colors.gray200,
                                        padding: "4px 8px",
                                        borderRadius: "3px",
                                        fontSize: "12px",
                                        fontFamily: "monospace",
                                    }}
                                >
                                    {transaction.id}
                                </code>
                            </DetailRow>

                            <DetailRow label="Offer ID">
                                <code
                                    style={{
                                        background: colors.gray200,
                                        padding: "4px 8px",
                                        borderRadius: "3px",
                                        fontSize: "12px",
                                        fontFamily: "monospace",
                                    }}
                                >
                                    {transaction.offerId}
                                </code>
                            </DetailRow>

                            <DetailRow label="Category">
                                <span
                                    style={{
                                        padding: "4px 12px",
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        background: "#fff3cd",
                                        color: "#856404",
                                    }}
                                >
                                    Settlement Deduction
                                </span>
                            </DetailRow>

                            <DetailRow label="Direction">
                                <span
                                    style={{
                                        padding: "4px 12px",
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        background: "#f8d7da",
                                        color: "#721c24",
                                    }}
                                >
                                    Funds Deducted
                                </span>
                            </DetailRow>

                            <DetailRow label="Amount Deducted">
                                <strong style={{ fontSize: "16px", color: colors.danger }}>
                                    -{formatAmount(Math.abs(amount))}
                                </strong>
                            </DetailRow>

                            <DetailRow label="Fee Breakdown">
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: "14px", color: colors.gray600 }}>Net Amount</span>
                                        <strong style={{ fontSize: "14px" }}>{formatAmount(Math.abs(amount))}</strong>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <span style={{ fontSize: "14px", color: colors.gray600 }}>Platform Fee</span>
                                        <strong style={{ fontSize: "14px", color: colors.danger }}>+{formatAmount(fee)}</strong>
                                    </div>
                                    <div style={{ 
                                        display: "flex", 
                                        justifyContent: "space-between", 
                                        alignItems: "center",
                                        paddingTop: "8px",
                                        borderTop: `1px solid ${colors.border}`
                                    }}>
                                        <span style={{ fontSize: "14px", fontWeight: 600 }}>Total Deducted</span>
                                        <strong style={{ fontSize: "16px", color: colors.danger }}>{formatAmount(totalDeducted)}</strong>
                                    </div>
                                </div>
                            </DetailRow>

                            <DetailRow label="Memo">
                                {transaction.memo}
                            </DetailRow>

                            {transaction.sender && (
                                <DetailRow label="Sender">
                                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                        {transaction.sender.fullName && (
                                            <span style={{ fontWeight: 600 }}>{transaction.sender.fullName}</span>
                                        )}
                                        {transaction.sender.email && (
                                            <span style={{ fontSize: "13px", color: colors.gray600 }}>
                                                {transaction.sender.email}
                                            </span>
                                        )}
                                    </div>
                                </DetailRow>
                            )}

                            <DetailRow label="Created At">
                                {formatDate(transaction.timestamp || transaction.createdAt)}
                            </DetailRow>
                        </>
                    ) : (
                        // Regular Transaction Layout
                        <>
                    <DetailRow label="Transaction ID">
                        {transaction.transactionId ? (
                            <a
                                href={getHashscanUrl(transaction.transactionId, hederaNetwork)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="detail-link"
                                style={{ fontFamily: "monospace", fontSize: "13px" }}
                            >
                                {transaction.transactionId} ↗
                            </a>
                        ) : (
                            "-"
                        )}
                    </DetailRow>

                    <DetailRow label="Network">
                        <span
                            style={{
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                                background: "#e8f4f8",
                                color: "#1a5276",
                            }}
                        >
                            {formatNetwork(hederaNetwork)}
                        </span>
                    </DetailRow>

                    <DetailRow label="Category">
                        <span
                            style={{
                                ...categoryStyle,
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                            }}
                        >
                            {transaction.category || "-"}
                        </span>
                    </DetailRow>

                    <DetailRow label="Direction">
                        <span
                            style={{
                                ...directionStyle,
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                            }}
                        >
                            {transaction.direction === "incoming" ? "↓ Incoming" : "↑ Outgoing"}
                        </span>
                    </DetailRow>

                    <DetailRow label="Type">
                        {transaction.type || "-"}
                    </DetailRow>

                    <DetailRow label="Currency / Token ID">
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <span style={{ fontSize: "14px", fontWeight: 600 }}>
                                {transaction.tokenId ? getCurrencyDisplay(transaction.tokenId) : "-"}
                            </span>
                            <code
                                style={{
                                    background: colors.gray200,
                                    padding: "4px 8px",
                                    borderRadius: "3px",
                                    fontSize: "11px",
                                    fontFamily: "monospace",
                                    alignSelf: "flex-start",
                                    color: colors.gray600,
                                }}
                            >
                                {transaction.tokenId || "-"}
                            </code>
                        </div>
                    </DetailRow>

                    <DetailRow label="From Account">
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <code
                                style={{
                                    background: colors.gray200,
                                    padding: "4px 8px",
                                    borderRadius: "3px",
                                    fontSize: "12px",
                                    fontFamily: "monospace",
                                    alignSelf: "flex-start",
                                }}
                            >
                                {transaction.fromAccountId || "-"}
                            </code>
                            {transaction.sender && (
                                <div style={{ fontSize: "13px", color: colors.gray600 }}>
                                    {transaction.sender.fullName && <span style={{ fontWeight: 600, display: "block" }}>{transaction.sender.fullName}</span>}
                                    {transaction.sender.email && <span>{transaction.sender.email}</span>}
                                </div>
                            )}
                        </div>
                    </DetailRow>

                    <DetailRow label="To Account">
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <code
                                style={{
                                    background: colors.gray200,
                                    padding: "4px 8px",
                                    borderRadius: "3px",
                                    fontSize: "12px",
                                    fontFamily: "monospace",
                                    alignSelf: "flex-start",
                                }}
                            >
                                {transaction.toAccountId || "-"}
                            </code>
                            {transaction.receiver && (
                                <div style={{ fontSize: "13px", color: colors.gray600 }}>
                                    {transaction.receiver.fullName && <span style={{ fontWeight: 600, display: "block" }}>{transaction.receiver.fullName}</span>}
                                    {transaction.receiver.email && <span>{transaction.receiver.email}</span>}
                                </div>
                            )}
                        </div>
                    </DetailRow>

                    <DetailRow label="Amount">
                        <strong style={{ fontSize: "16px", color: colors.primary }}>
                            {formatAmount(transaction.amount)}
                        </strong>
                    </DetailRow>

                    <DetailRow label="Fees">
                        {fee > 0 ? (
                            <span style={{ fontSize: "14px", color: colors.gray600 }}>
                                {formatAmount(fee)}
                            </span>
                        ) : (
                            "-"
                        )}
                    </DetailRow>

                    <DetailRow label="Total Amount">
                        <strong style={{ fontSize: "16px", color: colors.primary }}>
                            {formatAmount(total)}
                        </strong>
                    </DetailRow>

                    {transaction.exchangeRate && (
                        <DetailRow label="Exchange Rate">
                            {(() => {
                                // Parse exchange rate format: "1 0.0.7118026 = 1351.859219 0.0.7119522"
                                const rateStr = transaction.exchangeRate;
                                const match = rateStr.match(/(\d+(?:\.\d+)?)\s+(0\.0\.\d+)\s*=\s*(\d+(?:\.\d+)?)\s+(0\.0\.\d+)/);
                                
                                if (match) {
                                    const [, amount1, tokenId1, amount2, tokenId2] = match;
                                    const currency1 = getCurrencyDisplay(tokenId1);
                                    const currency2 = getCurrencyDisplay(tokenId2);
                                    
                                    return (
                                        <span style={{ fontSize: "14px" }}>
                                            {amount1} {currency1} = {amount2} {currency2}
                                        </span>
                                    );
                                }
                                
                                return rateStr;
                            })()}
                        </DetailRow>
                    )}

                    <DetailRow label="Status">
                        <span
                            style={{
                                ...statusStyle,
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: 600,
                            }}
                        >
                            {transaction.status || "-"}
                        </span>
                    </DetailRow>

                    {transaction.memo && (
                        <DetailRow label="Memo">
                            {transaction.memo}
                        </DetailRow>
                    )}

                    {/* Linked User Account */}
                    {wallet && (
                        <DetailRow label="Linked User Account">
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                {wallet.name && (
                                    <span style={{ fontWeight: 600 }}>{wallet.name}</span>
                                )}
                                {wallet.email && (
                                    <span style={{ fontSize: "13px", color: colors.gray600 }}>
                                        {wallet.email}
                                    </span>
                                )}
                            </div>
                        </DetailRow>
                    )}

                    <DetailRow label="Wallet ID">
                        <code
                            style={{
                                background: colors.gray200,
                                padding: "4px 8px",
                                borderRadius: "3px",
                                fontSize: "12px",
                                fontFamily: "monospace",
                            }}
                        >
                            {transaction.userWalletId || "-"}
                        </code>
                    </DetailRow>

                    <DetailRow label="Created At">
                        {formatDate(transaction.createdAt)}
                    </DetailRow>

                    <DetailRow label="Updated At">
                        {formatDate(transaction.updatedAt)}
                    </DetailRow>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransactionDetail;
